<template>
  <div v-if="showModal" class="doctor-modal-backdrop" @click.self="showModal = false">
    <div class="doctor-modal-card shadow-lg animate-scale-up">
      <div class="modal-header-custom border-0 pb-0">
        <div class="d-flex align-items-center gap-3">
          <div class="modal-icon-badge shadow-sm">
            <i class="fas fa-user-md text-white"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bolder text-dark">
              {{ editingDoctor ? `Edit Doctor Availability: ${editingDoctor.name}` : 'Confirm Consultation Session' }}
            </h5>
            <p class="text-xs text-secondary mb-0">
              {{ editingDoctor ? `Update consultation timings, status, room, and slot duration for ${editingDoctor.name}.` : 'Set your consultation timings and patient slot duration for today.' }}
            </p>
          </div>
        </div>
        <button type="button" class="btn-close text-secondary" @click="showModal = false" aria-label="Close"></button>
      </div>

      <div class="modal-body-custom pt-3">
        <!-- Availability Status -->
        <div class="form-group mb-3">
          <label class="form-label text-xs font-weight-bold text-dark mb-2">
            <i class="fas fa-user-check text-teal me-1"></i> Doctor Availability Status:
          </label>
          <div class="d-flex gap-2">
            <button 
              type="button" 
              class="btn btn-sm flex-fill mb-0 text-xs font-weight-bold py-2 border transition-all"
              :class="form.status === 'Available' ? 'bg-info-soft text-info border-info shadow-sm' : 'bg-white text-secondary'" 
              @click="form.status = 'Available'"
            >
              🟢 Available
            </button>
            <button 
              type="button" 
              class="btn btn-sm flex-fill mb-0 text-xs font-weight-bold py-2 border transition-all"
              :class="form.status === 'On Break' ? 'bg-warning-soft text-warning border-warning shadow-sm' : 'bg-white text-secondary'" 
              @click="form.status = 'On Break'"
            >
              🟡 On Break
            </button>
            <button 
              type="button" 
              class="btn btn-sm flex-fill mb-0 text-xs font-weight-bold py-2 border transition-all"
              :class="form.status === 'Off Duty' ? 'bg-secondary-soft text-secondary border-secondary shadow-sm' : 'bg-white text-secondary'" 
              @click="form.status = 'Off Duty'"
            >
              ⚪ Off Duty
            </button>
          </div>
        </div>

        <!-- Duration per patient -->
        <div class="form-group mb-3">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <label class="form-label text-xs font-weight-bold text-dark mb-0">
              <i class="fas fa-stopwatch text-teal me-1"></i> Consultation Duration per Patient:
            </label>
            <span class="badge bg-teal-soft text-teal text-xxs font-weight-bold">
              {{ form.slot_duration_minutes }} Mins / patient
            </span>
          </div>

          <!-- Dropdown & Direct Number Input -->
          <div class="row g-2 align-items-center mb-2">
            <div class="col-8">
              <select v-model.number="form.slot_duration_minutes" class="form-select form-control-sm time-input shadow-none text-xs font-weight-bold">
                <option :value="10">⏱️ 10 Minutes per patient</option>
                <option :value="15">⏱️ 15 Minutes per patient</option>
                <option :value="20">⏱️ 20 Minutes per patient</option>
                <option :value="25">⏱️ 25 Minutes per patient</option>
                <option :value="30">⏱️ 30 Minutes per patient</option>
                <option :value="40">⏱️ 40 Minutes per patient</option>
                <option :value="45">⏱️ 45 Minutes per patient</option>
                <option :value="50">⏱️ 50 Minutes per patient</option>
                <option :value="60">⏱️ 60 Minutes (1 Hour)</option>
                <option v-if="![10, 15, 20, 25, 30, 40, 45, 50, 60].includes(form.slot_duration_minutes)" :value="form.slot_duration_minutes">
                  ⏱️ {{ form.slot_duration_minutes }} Minutes (Custom)
                </option>
              </select>
            </div>
            <div class="col-4">
              <div class="input-group input-group-sm">
                <input 
                  type="number" 
                  min="1" 
                  max="240" 
                  class="form-control form-control-sm time-input shadow-none text-xs text-center font-weight-bold" 
                  v-model.number="form.slot_duration_minutes" 
                  placeholder="Mins"
                />
                <span class="input-group-text text-xxs bg-light">mins</span>
              </div>
            </div>
          </div>

          <!-- Quick Selector Buttons: 10, 15, 20, 25, 30, 45, 60 -->
          <div class="d-flex gap-1 flex-wrap">
            <button 
              v-for="slot in [10, 15, 20, 25, 30, 45, 60]" 
              :key="slot"
              type="button" 
              class="btn btn-xs py-1 px-2 mb-0 font-weight-bold border transition-all rounded-pill"
              :class="form.slot_duration_minutes === slot ? 'btn-teal text-white shadow-sm' : 'bg-light text-secondary border-light'" 
              @click="form.slot_duration_minutes = slot"
            >
              {{ slot }}m
            </button>
          </div>
        </div>

        <!-- Shift Timing -->
        <div class="row g-2 mb-3">
          <div class="col-6">
            <label class="form-label text-xs font-weight-bold text-dark mb-1">
              <i class="fas fa-clock text-teal me-1"></i> Shift Start Time:
            </label>
            <input 
              type="time" 
              class="form-control form-control-sm time-input shadow-none" 
              v-model="form.start_time" 
              required 
            />
          </div>
          <div class="col-6">
            <label class="form-label text-xs font-weight-bold text-dark mb-1">
              <i class="fas fa-hourglass-end text-teal me-1"></i> Shift End Time:
            </label>
            <input 
              type="time" 
              class="form-control form-control-sm time-input shadow-none" 
              v-model="form.end_time" 
              required 
            />
          </div>
        </div>

        <!-- Department / Speciality (Default Display, No Dropdown) -->
        <div class="form-group mb-3">
          <label class="form-label text-xs font-weight-bold text-dark mb-1">
            <i class="fas fa-hospital text-teal me-1"></i> Department / Speciality:
          </label>
          <div class="d-flex align-items-center justify-content-between bg-light border rounded px-3 py-2">
            <div class="d-flex align-items-center gap-2">
              <i class="fas fa-stethoscope text-teal"></i>
              <span class="text-xs font-weight-bold text-dark">{{ displayDepartment }}</span>
            </div>
            <span class="badge bg-teal-soft text-teal text-xxs font-weight-bold">Default</span>
          </div>
          <input type="hidden" v-model="form.department" />
        </div>

        <!-- Room Allocation Selection -->
        <div class="form-group mb-3">
          <label class="form-label text-xs font-weight-bold text-dark mb-1">
            <i class="fas fa-door-open text-teal me-1"></i> Room / Counter Allocation:
          </label>
          <input 
            type="text" 
            class="form-control form-control-sm time-input shadow-none text-xs" 
            placeholder="e.g. ROOM 04 or Counter 1"
            v-model="form.room"
          />
        </div>

        <!-- Summary Info Box -->
        <div class="summary-box p-3 rounded-3 mb-2 bg-light d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center gap-2">
            <i class="fas fa-info-circle text-teal"></i>
            <small class="text-xs text-muted">
              Estimated capacity: 
              <strong class="text-dark">{{ estimatedCapacity }} patients</strong> during shift.
            </small>
          </div>
          <span class="badge bg-teal-soft text-teal text-xxs font-weight-bold">
            {{ form.slot_duration_minutes }}m / patient
          </span>
        </div>
      </div>

      <div class="modal-footer-custom pt-2 border-0 d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-sm btn-light mb-0 px-3" @click="showModal = false">
          Later
        </button>
        <button 
          type="button" 
          class="btn btn-sm btn-teal text-white mb-0 shadow-sm px-3 d-flex align-items-center gap-1 font-weight-bold" 
          :disabled="loading" 
          @click="saveAvailability"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <span v-else>🟢</span>
          {{ loading ? 'Saving...' : (editingDoctor ? 'Save Doctor Availability' : 'Start Consultation Session') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DoctorShiftModal',
  data() {
    const savedDept = localStorage.getItem('doctorDepartment') || localStorage.getItem('department') || localStorage.getItem('branch_name') || '';

    let initialForm = {
      slot_duration_minutes: 30,
      start_time: '09:00',
      end_time: '13:00',
      department: savedDept,
      room: '',
      status: 'Available'
    };
    try {
      const saved = localStorage.getItem('doctorShiftSession');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.start_time) initialForm.start_time = parsed.start_time;
        if (parsed.end_time) initialForm.end_time = parsed.end_time;
        if (parsed.slot_duration_minutes) initialForm.slot_duration_minutes = Number(parsed.slot_duration_minutes);
        if (parsed.department) initialForm.department = parsed.department;
        if (parsed.room) initialForm.room = parsed.room;
        if (parsed.status) initialForm.status = parsed.status;
      }
    } catch (e) {
      console.warn("Could not read doctorShiftSession in data():", e);
    }

    return {
      showModal: false,
      loading: false,
      departmentsList: [],
      editingDoctor: null,
      form: initialForm
    };
  },
  computed: {
    apiUrl() {
      return process.env.VUE_APP_API_BASE_URL || '/api';
    },
    displayDepartment() {
      if (this.form && this.form.department) return this.form.department;
      if (this.editingDoctor && (this.editingDoctor.department || this.editingDoctor.speciality)) {
        return this.editingDoctor.department || this.editingDoctor.speciality;
      }
      const localDept = localStorage.getItem('doctorDepartment') || localStorage.getItem('department') || localStorage.getItem('branch_name');
      if (localDept) return localDept;
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        const uDept = u.department || u.department_name || u.speciality || u.specialization || (u.branch && u.branch.branch_name);
        if (uDept) return uDept;
      } catch (e) {
        console.warn("Could not read user department in displayDepartment:", e);
      }
      return 'General OPD';
    },
    estimatedCapacity() {
      if (!this.form.start_time || !this.form.end_time || !this.form.slot_duration_minutes) return 0;
      try {
        const [startH, startM] = this.form.start_time.split(':').map(Number);
        const [endH, endM] = this.form.end_time.split(':').map(Number);
        const totalMinutes = (endH * 60 + endM) - (startH * 60 + startM);
        if (totalMinutes <= 0) return 0;
        return Math.floor(totalMinutes / this.form.slot_duration_minutes);
      } catch (e) {
        return 0;
      }
    },
    allAvailableDepartments() {
      const list = [...(this.departmentsList || [])];
      if (this.form.department && !list.some(d => d.name.toLowerCase() === this.form.department.toLowerCase())) {
        list.unshift({ id: 'selected', name: this.form.department });
      }
      return list;
    }
  },
  mounted() {
    this.checkDoctorShift();
  },
  methods: {
    getAuthToken() {
      return localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('admin_token') || '';
    },
    formatToTimeInput(str) {
      if (!str) return '09:00';
      const trimmed = String(str).trim();
      if (/^([01]\d|2[0-3]):[0-5]\d$/.test(trimmed)) {
        return trimmed;
      }
      const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = match[2];
        const modifier = match[3].toLowerCase();
        if (modifier === 'pm' && hours < 12) hours += 12;
        if (modifier === 'am' && hours === 12) hours = 0;
        return `${String(hours).padStart(2, '0')}:${minutes}`;
      }
      return trimmed.length >= 5 ? trimmed.substring(0, 5) : '09:00';
    },
    async checkDoctorShift() {
      // 1. Check local session and global registry
      const currentUserId = String(localStorage.getItem('id') || '');
      const currentUserEmail = (localStorage.getItem('adminEmail') || localStorage.getItem('email') || '').toLowerCase().trim();
      const currentUserName = (localStorage.getItem('userName') || localStorage.getItem('name') || '').trim();
      const cleanDocName = currentUserName.toLowerCase().replace(/^dr\.?\s*/i, '').trim();

      let savedSession = null;
      try {
        const s = localStorage.getItem('doctorShiftSession');
        if (s) savedSession = JSON.parse(s);
      } catch (e) {
        console.warn("Could not parse doctorShiftSession:", e);
      }

      let allShifts = {};
      try {
        allShifts = JSON.parse(localStorage.getItem('hospitalDoctorShifts') || '{}');
      } catch (e) {
        allShifts = {};
      }

      let registryItem = (currentUserId && allShifts[currentUserId]) || (currentUserEmail && allShifts[currentUserEmail]) || (cleanDocName && allShifts[cleanDocName]);
      if (!registryItem && allShifts) {
        registryItem = Object.values(allShifts).find(item => 
          item && (
            (currentUserId && String(item.doctor_id) === currentUserId) ||
            (currentUserEmail && item.doctor_email && item.doctor_email.toLowerCase() === currentUserEmail) ||
            (cleanDocName && item.doctor_name && item.doctor_name.toLowerCase().replace(/^dr\.?\s*/i, '').trim().includes(cleanDocName))
          )
        );
      }

      const activeData = registryItem || savedSession;
      if (activeData) {
        this.form = {
          ...this.form,
          slot_duration_minutes: Number(activeData.slot_duration_minutes) || this.form.slot_duration_minutes,
          start_time: this.formatToTimeInput(activeData.start_time),
          end_time: this.formatToTimeInput(activeData.end_time),
          department: activeData.department || this.form.department,
          room: activeData.room || this.form.room,
          status: activeData.status || this.form.status
        };
      }

      // 2. Fetch active branches/departments from backend API
      await this.fetchDepartments();

      const token = this.getAuthToken();
      if (token) {
        if (!this.form.department && this.departmentsList.length > 0) {
          const matched = this.departmentsList.find(b => {
            if (currentUserId && b.user_id && String(b.user_id) === String(currentUserId)) return true;
            if (b.contact_person && cleanDocName) {
              const bPerson = b.contact_person.toLowerCase().replace(/^dr\.?\s*/i, '');
              if (bPerson.includes(cleanDocName) || cleanDocName.includes(bPerson)) return true;
            }
            return false;
          });
          if (matched) {
            this.form.department = matched.name;
          }
        }

        try {
          const res = await axios.get(`${this.apiUrl}/v1/admin/doctor/my-shift`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data?.data) {
            this.form.slot_duration_minutes = Number(res.data.data.slot_duration_minutes) || this.form.slot_duration_minutes;
            if (res.data.data.start_time) this.form.start_time = this.formatToTimeInput(res.data.data.start_time);
            if (res.data.data.end_time) this.form.end_time = this.formatToTimeInput(res.data.data.end_time);
            if (res.data.data.department) this.form.department = res.data.data.department;
            if (res.data.data.room) this.form.room = res.data.data.room;
            if (res.data.data.status) this.form.status = res.data.data.status;
            localStorage.setItem('doctorShiftSession', JSON.stringify(this.form));
          }
        } catch (e) {
          console.warn("Shift fetch skipped:", e);
        }
      }

      const userRole = (localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase();
      const instType = String(localStorage.getItem('installationType') || '').toLowerCase();

      // Only pop up automatically on first login of the day if NO shift session has been saved yet
      if (!savedSession && !registryItem && (userRole.includes('doc') || userRole.includes('attendant') || instType.includes('medical'))) {
        this.showModal = true;
      }
    },
    async saveAvailability() {
      this.loading = true;
      try {
        const token = this.getAuthToken();
        const slotMins = Number(this.form.slot_duration_minutes) || 15;
        const startTime = this.formatToTimeInput(this.form.start_time);
        const endTime = this.formatToTimeInput(this.form.end_time);
        const shiftRange = `${startTime} - ${endTime}`;
        const finalDept = this.form.department || this.displayDepartment || 'General OPD';

        // Preserve active token on shift update
        const existingCurrentToken = this.editingDoctor?.currentToken || localStorage.getItem('current_token') || '--';

        const payload = {
          slot_duration_minutes: slotMins,
          start_time: startTime,
          end_time: endTime,
          shift: shiftRange,
          department: finalDept,
          speciality: finalDept,
          room: this.form.room || '',
          status: this.form.status || 'Available',
          availability_status: this.form.status || 'Available',
          current_token: existingCurrentToken,
          currentToken: existingCurrentToken
        };

        let allShifts = {};
        try {
          allShifts = JSON.parse(localStorage.getItem('hospitalDoctorShifts') || '{}');
        } catch (e) {
          allShifts = {};
          console.warn("Could not parse hospitalDoctorShifts:", e);
        }

        const currentUserId = String(localStorage.getItem('id') || '');
        const currentUserEmail = String(localStorage.getItem('adminEmail') || localStorage.getItem('email') || '').toLowerCase().trim();
        const currentUserName = String(localStorage.getItem('userName') || localStorage.getItem('name') || '').trim();
        const cleanCurrentUserName = currentUserName.toLowerCase().replace(/^dr\.?\s*/i, '').trim();

        let finalDocId = '';
        let finalDocName = '';
        let finalDocEmail = '';

        if (this.editingDoctor) {
          finalDocId = String(this.editingDoctor.user_id || this.editingDoctor.id || '');
          finalDocName = this.editingDoctor.doctor_name || this.editingDoctor.name || '';
          finalDocEmail = (this.editingDoctor.email || '').toLowerCase().trim();
          payload.doctor = this.editingDoctor;
        } else {
          finalDocId = currentUserId || '1';
          finalDocName = currentUserName || 'Doctor';
          finalDocEmail = currentUserEmail;
        }

        if (!finalDocId) finalDocId = currentUserId || '1';
        if (!finalDocName) finalDocName = currentUserName || 'Doctor';
        if (!finalDocEmail) finalDocEmail = currentUserEmail;

        const formattedDocName = finalDocName.startsWith('Dr.') ? finalDocName : `Dr. ${finalDocName.replace(/^dr\.?\s*/i, '')}`;
        payload.doctor_id = finalDocId;
        payload.user_id = finalDocId;
        payload.doctor_name = formattedDocName;
        payload.doctor_email = finalDocEmail;

        const cleanFinalName = finalDocName.toLowerCase().replace(/^dr\.?\s*/i, '').trim();
        const docCleanName = this.editingDoctor?.name ? this.editingDoctor.name.toLowerCase().replace(/^dr\.?\s*/i, '').trim() : '';
        const isEditingOtherDoctor = Boolean(this.editingDoctor && String(this.editingDoctor.id || this.editingDoctor.user_id) !== currentUserId);

        let targetCounterId = this.editingDoctor?.counter_id || null;

        // Auto-match doctor's counter from /v1/admin/counter if not directly known
        if (token) {
          try {
            const countersRes = await axios.get(`${this.apiUrl}/v1/admin/counter`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const countersList = Array.isArray(countersRes.data) ? countersRes.data : (countersRes.data?.data || []);
            let matched = null;

            // 1. Match by room name entered in modal
            if (this.form.room) {
              const cleanFormRoom = this.form.room.trim().toLowerCase();
              matched = countersList.find(c => c.name && c.name.trim().toLowerCase() === cleanFormRoom);
              if (!matched) {
                const digits = cleanFormRoom.replace(/[^0-9]/g, '');
                if (digits) matched = countersList.find(c => c.name && c.name.replace(/[^0-9]/g, '') === digits);
              }
            }

            // 2. Match by doctor user ID
            if (!matched && finalDocId) {
              matched = countersList.find(c => (c.user_id && String(c.user_id) === String(finalDocId)) || (c.user && String(c.user.id) === String(finalDocId)));
            }

            // 3. Match by doctor email
            if (!matched && finalDocEmail) {
              matched = countersList.find(c => (c.email && c.email.toLowerCase() === finalDocEmail.toLowerCase()) || (c.user?.email && c.user.email.toLowerCase() === finalDocEmail.toLowerCase()));
            }

            // 4. Match by doctor attendant name
            if (!matched && cleanFinalName) {
              matched = countersList.find(c => {
                if (c.attendant_name) {
                  const att = c.attendant_name.toLowerCase().replace(/^dr\.?\s*/i, '');
                  if (att.includes(cleanFinalName) || cleanFinalName.includes(att)) return true;
                }
                return false;
              });
            }

            if (matched) {
              targetCounterId = matched.id;
              if (!isEditingOtherDoctor) {
                localStorage.setItem('counter_id', matched.id);
              }
            }
          } catch (e) {
            console.warn("Could not lookup doctor counter:", e);
          }
        }

        payload.counter_id = targetCounterId;

        // If editing another doctor as admin, isolate keys strictly to that doctor
        const keysToStore = isEditingOtherDoctor ? [
          finalDocId,
          this.editingDoctor?.user_id ? String(this.editingDoctor.user_id) : null,
          this.editingDoctor?.id ? String(this.editingDoctor.id) : null,
          finalDocEmail,
          this.editingDoctor?.email ? String(this.editingDoctor.email).toLowerCase().trim() : null,
          cleanFinalName,
          docCleanName,
          payload.doctor_name.toLowerCase().trim()
        ].filter(Boolean) : [
          finalDocId,
          currentUserId,
          this.editingDoctor?.id ? String(this.editingDoctor.id) : null,
          finalDocEmail,
          currentUserEmail,
          this.editingDoctor?.email ? String(this.editingDoctor.email).toLowerCase().trim() : null,
          cleanFinalName,
          docCleanName,
          cleanCurrentUserName,
          payload.doctor_name.toLowerCase().trim()
        ].filter(Boolean);

        keysToStore.forEach(k => {
          try {
            localStorage.setItem(`doctorShift_${k}`, JSON.stringify(payload));
            allShifts[k] = payload;
          } catch (e) {
            console.warn("Storage write error for key:", k, e);
          }
        });

        try {
          localStorage.setItem('hospitalDoctorShifts', JSON.stringify(allShifts));
          if (!isEditingOtherDoctor) {
            localStorage.setItem('doctorShiftSession', JSON.stringify(payload));
            if (payload.department) {
              localStorage.setItem('doctorDepartment', payload.department);
              localStorage.setItem('department', payload.department);
            }
            if (payload.room) {
              localStorage.setItem('doctorRoom', payload.room);
            }
          }
        } catch (e) {
          console.warn("Could not save session shift:", e);
        }

        // Broadcast storage & custom events for instant cross-tab sync
        try {
          localStorage.setItem('doctorAvailabilityLastUpdated', String(Date.now()));
          window.dispatchEvent(new CustomEvent('doctor-availability-changed', { detail: { ...payload, is_shift_update: true } }));
          window.dispatchEvent(new CustomEvent('doctor-status-synced', { detail: { status: payload.status, currentToken: existingCurrentToken } }));
        } catch (e) {
          console.warn("Could not dispatch availability event:", e);
        }

        // Attempt backend persist to set-timing (saves start_time, end_time, slot_duration_minutes, availability_status into counters table)
        try {
          const res = await axios.post(`${this.apiUrl}/v1/admin/doctor/set-timing`, payload, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          this.$emit('shift-updated', res.data?.data || payload);
        } catch (apiErr) {
          console.warn("Backend set-timing endpoint fallback:", apiErr.message);
          this.$emit('shift-updated', payload);
        }

        // Secondary sync: ONLY update counter table directly if counter is dedicated exclusively to this doctor
        const role = (localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase();
        const isDoc = role.includes('doctor') || role.includes('medical') || role.includes('physician');
        const isDedicatedCounter = this.editingDoctor && this.editingDoctor.user_id && String(this.editingDoctor.user_id) === String(payload.user_id);
        if (payload.counter_id && !isDoc && isDedicatedCounter) {
          try {
            await axios.put(`${this.apiUrl}/v1/admin/counter/update/${payload.counter_id}`, {
              start_time: payload.start_time,
              end_time: payload.end_time,
              slot_duration_minutes: payload.slot_duration_minutes,
              availability_status: payload.status,
              current_token: existingCurrentToken,
              status: payload.status === 'Available' ? 1 : 0
            }, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
          } catch (counterErr) {
            console.warn("Counter update skipped:", counterErr.message);
          }
        }

        // Secondary sync: update user record with timing and availability status
        if (payload.user_id) {
          try {
            await axios.post(`${this.apiUrl}/v1/admin/user/update/${payload.user_id}`, {
              start_time: payload.start_time,
              end_time: payload.end_time,
              shift: `${payload.start_time} - ${payload.end_time}`,
              slot_duration_minutes: payload.slot_duration_minutes,
              availability_status: payload.status,
              doctor_status: payload.status,
              current_token: existingCurrentToken,
              status: 1
            }, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
          } catch (userErr) {
            console.warn("User update sync skipped:", userErr.message);
          }
        }

        // Supplementary status update
        try {
          await axios.post(`${this.apiUrl}/v1/admin/doctor/status`, {
            availability_status: payload.status || payload.availability_status || 'Available',
            status: payload.status || payload.availability_status || 'Available',
            current_token: existingCurrentToken,
            currentToken: existingCurrentToken,
            user_id: payload.user_id || payload.doctor_id,
            doctor_name: payload.doctor_name,
            counter_id: payload.counter_id
          }, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
        } catch (statusErr) {
          console.warn("Doctor status endpoint skipped:", statusErr.message);
        }

        this.showModal = false;
        this.$emit('session-started', { ...payload });
      } finally {
        this.loading = false;
      }
    },
    async fetchCurrentShift() {
      try {
        const token = this.getAuthToken();
        if (!token) return;
        const response = await axios.get(
          `${this.apiUrl}/v1/admin/doctor/my-shift`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data && response.data.success && response.data.data) {
          const data = response.data.data;
          if (data.start_time) this.form.start_time = this.formatToTimeInput(data.start_time);
          if (data.end_time) this.form.end_time = this.formatToTimeInput(data.end_time);
          if (data.slot_duration_minutes) this.form.slot_duration_minutes = Number(data.slot_duration_minutes);
          if (data.availability_status || data.status) this.form.status = data.availability_status || data.status;
          if (data.department) this.form.department = data.department;
          if (data.room) this.form.room = data.room;
        }
      } catch (err) {
        console.warn('Shift fetch skipped or not available:', err.message);
      }
    },
    async fetchDepartments() {
      const token = this.getAuthToken();
      if (!token) return;
      try {
        const bRes = await axios.get(`${this.apiUrl}/v1/admin/branches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = Array.isArray(bRes.data) ? bRes.data : (bRes.data?.data || bRes.data?.branches || []);
        if (Array.isArray(list) && list.length > 0) {
          this.departmentsList = list
            .map(b => ({
              id: b.id,
              name: (b.branch_name || b.department_name || b.name || '').trim()
            }))
            .filter(d => d.name);
        }
      } catch (e) {
        console.warn("Branches fetch skipped:", e);
      }
    },
    async open(targetDoctor = null) {
      this.editingDoctor = targetDoctor;
      await this.fetchDepartments();

      if (targetDoctor) {
        let start = targetDoctor.start_time || '09:00';
        let end = targetDoctor.end_time || '17:00';
        if ((!targetDoctor.start_time || !targetDoctor.end_time) && targetDoctor.shift && targetDoctor.shift.includes('-')) {
          const parts = targetDoctor.shift.split('-').map(s => s.trim());
          if (parts[0]) start = parts[0];
          if (parts[1]) end = parts[1];
        }

        let slot = Number(targetDoctor.slot_duration_minutes) || 15;
        let room = targetDoctor.room || '';
        let dept = targetDoctor.speciality || targetDoctor.department || this.displayDepartment || '';
        let status = targetDoctor.status || 'Available';

        const docId = String(targetDoctor.id || targetDoctor.user_id || '');
        const docEmail = (targetDoctor.email || '').toLowerCase().trim();
        const docName = (targetDoctor.name || targetDoctor.doctor_name || '').toLowerCase().replace(/^dr\.?\s*/i, '').trim();

        let allShifts = {};
        try {
          allShifts = JSON.parse(localStorage.getItem('hospitalDoctorShifts') || '{}');
        } catch (e) {
          allShifts = {};
          console.warn("Could not parse hospitalDoctorShifts:", e);
        }

        let p = null;
        try {
          const byId = docId ? localStorage.getItem(`doctorShift_${docId}`) : null;
          const byEmail = docEmail ? localStorage.getItem(`doctorShift_${docEmail}`) : null;
          const byName = docName ? localStorage.getItem(`doctorShift_${docName}`) : null;
          let fromRegistry = (docId && allShifts[docId]) || (docEmail && allShifts[docEmail]) || (docName && allShifts[docName]);
          if (!fromRegistry && allShifts) {
            fromRegistry = Object.values(allShifts).find(item => 
              item && (
                (docId && (String(item.doctor_id) === docId || String(item.user_id) === docId || String(item.id) === docId)) ||
                (docEmail && item.doctor_email && item.doctor_email.toLowerCase() === docEmail) ||
                (docName && item.doctor_name && item.doctor_name.toLowerCase().replace(/^dr\.?\s*/i, '').trim().includes(docName))
              )
            );
          }

          if (byId) p = JSON.parse(byId);
          else if (byEmail) p = JSON.parse(byEmail);
          else if (byName) p = JSON.parse(byName);
          else if (fromRegistry) p = fromRegistry;
        } catch (e) {
          console.warn("Could not parse doctor shift:", e);
        }

        // Check fallback doctorShiftSession ONLY if matching this doctor
        if (!p) {
          try {
            const sessionStr = localStorage.getItem('doctorShiftSession');
            if (sessionStr) {
              const session = JSON.parse(sessionStr);
              if (
                (session.doctor_email && docEmail && session.doctor_email.toLowerCase() === docEmail) ||
                (session.doctor_name && docName && session.doctor_name.toLowerCase().replace(/^dr\.?\s*/i, '').trim() === docName) ||
                (session.doctor_id && docId && String(session.doctor_id) === docId) ||
                (session.user_id && docId && String(session.user_id) === docId)
              ) {
                p = session;
              }
            }
          } catch (e) {
            console.warn("Could not parse doctorShiftSession:", e);
          }
        }

        if (p) {
          if (p.start_time) start = p.start_time;
          if (p.end_time) end = p.end_time;
          if (p.shift && (!p.start_time || !p.end_time) && p.shift.includes('-')) {
            const parts = p.shift.split('-').map(s => s.trim());
            if (parts[0]) start = parts[0];
            if (parts[1]) end = parts[1];
          }
          if (p.slot_duration_minutes) slot = Number(p.slot_duration_minutes);
          if (p.room) room = p.room;
          if (p.department) dept = p.department;
          if (p.status) status = p.status;
        }

        this.form = {
          slot_duration_minutes: slot,
          start_time: this.formatToTimeInput(start),
          end_time: this.formatToTimeInput(end),
          department: dept || this.displayDepartment,
          room: room,
          status: status
        };
      } else {
        const currentUserId = String(localStorage.getItem('id') || '');
        const currentUserEmail = (localStorage.getItem('adminEmail') || localStorage.getItem('email') || '').toLowerCase().trim();
        const currentUserName = (localStorage.getItem('userName') || localStorage.getItem('name') || '').trim();
        const cleanDocName = currentUserName.toLowerCase().replace(/^dr\.?\s*/i, '').trim();

        let allShifts = {};
        try {
          allShifts = JSON.parse(localStorage.getItem('hospitalDoctorShifts') || '{}');
        } catch (e) {
          allShifts = {};
        }

        let p = (currentUserId && allShifts[currentUserId]) || (currentUserEmail && allShifts[currentUserEmail]) || (cleanDocName && allShifts[cleanDocName]);
        if (!p && allShifts) {
          p = Object.values(allShifts).find(item => 
            item && (
              (currentUserId && String(item.doctor_id) === currentUserId) ||
              (currentUserEmail && item.doctor_email && item.doctor_email.toLowerCase() === currentUserEmail) ||
              (cleanDocName && item.doctor_name && item.doctor_name.toLowerCase().replace(/^dr\.?\s*/i, '').trim().includes(cleanDocName))
            )
          );
        }

        if (!p) {
          const saved = localStorage.getItem('doctorShiftSession');
          if (saved) {
            try {
              p = JSON.parse(saved);
            } catch (e) {
              console.warn("Could not read doctorShiftSession in open():", e);
            }
          }
        }

        if (p) {
          this.form = {
            slot_duration_minutes: Number(p.slot_duration_minutes) || this.form.slot_duration_minutes,
            start_time: this.formatToTimeInput(p.start_time),
            end_time: this.formatToTimeInput(p.end_time),
            department: p.department || this.form.department,
            room: p.room || this.form.room,
            status: p.status || this.form.status
          };
        }

        const savedRoom = localStorage.getItem('doctorRoom');
        if (savedRoom && !this.form.room) {
          this.form.room = savedRoom;
        }
      }

      this.showModal = true;
    }
  }
};
</script>

<style scoped>
.doctor-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.doctor-modal-card {
  background: #ffffff;
  border-radius: 1.25rem;
  width: 100%;
  max-width: 520px;
  padding: 1.75rem;
  box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.animate-scale-up {
  animation: scaleUp 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.btn-teal {
  background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
  border: none;
}

.btn-teal:hover {
  background: linear-gradient(135deg, #0f766e 0%, #0d9488 100%);
}

.bg-teal-soft {
  background-color: rgba(13, 148, 136, 0.12);
}

.text-teal {
  color: #0d9488 !important;
}

.duration-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.duration-btn {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.6rem 0.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.duration-btn:hover {
  border-color: #14b8a6;
  background: #f0fdfa;
}

.duration-btn.active {
  background: #f0fdfa;
  border-color: #0d9488;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.2);
}

.duration-num {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
}

.duration-btn.active .duration-num {
  color: #0d9488;
}

.duration-unit {
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
}

.time-input {
  border: 1.5px solid #e2e8f0;
  border-radius: 0.6rem;
  padding: 0.45rem 0.65rem;
  font-weight: 600;
  font-size: 0.85rem;
}

.time-input:focus {
  border-color: #0d9488;
}

.summary-box {
  border: 1px solid #e2e8f0;
}
</style>